import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useRequest } from '@frontend/api';
import type { QueryFunction } from '@frontend/api';

import {
  UseAsyncTaskStatusParams,
  AsyncTaskStatusQueryKey,
  AsyncTaskStatusesResponseData,
  isAsyncTaskSuccess,
  isAsyncTaskFailure,
} from './types';
import { getAsyncTaskStatusQueryKey } from './util';

export const useAsyncTaskStatuses: QueryFunction<
  AsyncTaskStatusesResponseData,
  [UseAsyncTaskStatusParams],
  AsyncTaskStatusQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const { taskIds, onFinishTasks, customRefetchInterval } = params;

  const [finishedTasks, setFinishedTasks] = useState<string[]>([]);

  const queryKey = getAsyncTaskStatusQueryKey(params);
  const request = useRequest(requestOptions);
  const enabled = queryOptions?.enabled ?? taskIds.length > 0;

  const queryResult = useQuery<
    AsyncTaskStatusesResponseData,
    unknown,
    AsyncTaskStatusesResponseData,
    AsyncTaskStatusQueryKey
  >({
    refetchInterval: (data, query) => {
      if (!enabled || query.state.dataUpdateCount === 60) {
        return false;
      }
      return customRefetchInterval ?? 5000;
    },
    enabled,
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) =>
      request(`/suppliers/${params.groupId}/task_status`, {
        init: {
          method: 'GET',
          signal,
        },
        query: { taskIds: params.taskIds },
        retryOptions: {
          maxRetries: 0,
        },
      }),
  });

  const { data: { taskStatuses } = {} } = queryResult;

  useEffect(() => {
    if (taskStatuses) {
      const successes = taskStatuses.filter(isAsyncTaskSuccess);
      const failures = taskStatuses.filter(isAsyncTaskFailure);
      const finished = [...successes, ...failures].map(
        (status) => status.taskId,
      );
      const previouslyFinishedTasks = finishedTasks;
      const newlyFinishedTasks = finished.filter(
        (task) => !previouslyFinishedTasks.includes(task),
      );
      if (newlyFinishedTasks.length > 0) {
        onFinishTasks({ successes, failures });
        setFinishedTasks(finished);
      }
    }
  }, [onFinishTasks, taskStatuses, setFinishedTasks, finishedTasks]);

  return queryResult;
};
